/**
 * QuickReferenceRail Component - MA-ARCH-162
 * 
 * Extracted from ScriptPlay.jsx - Icon rail for the Quick Reference panel.
 * Contains toggle button and view selector icons.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  PanelRightClose,
  PanelRightOpen,
  User,
  List,
  Calculator,
  Calendar as CalendarIcon,
  StickyNote,
} from 'lucide-react';

const SELECTED_ICON_CLASS = "bg-indigo-500/90 text-white ring-indigo-400/20 hover:bg-indigo-500";
const IDLE_ICON_CLASS = "bg-white/10 text-slate-100 ring-white/10 hover:bg-white/15";

/**
 * @param {Object} props
 * @param {boolean} props.collapsed - Whether the panel is collapsed
 * @param {string} props.currentView - Current view ('prospect_info', 'quote_sheet', 'placeholder_2', etc.)
 * @param {string} props.quoteToolId - ID for quote sheet view
 * @param {Function} props.onToggleCollapse - Callback to toggle collapse
 * @param {Function} props.onViewChange - Callback when view changes: (viewId) => void
 */
export function QuickReferenceRail({
  collapsed,
  currentView,
  quoteToolId,
  onToggleCollapse,
  onViewChange,
}) {
  const handleViewClick = (viewId) => {
    onViewChange(viewId);
  };

  return (
    <div
      className="absolute right-0 top-0 flex h-full w-[76px] flex-col items-center gap-2 border-l border-white/10 p-3"
      data-testid="quick-reference-rail"
    >
      <div
        className="flex flex-col items-center gap-3 pt-1"
        data-testid="prospect-panel-icon-selector"
      >
        {/* Collapse/Expand Toggle */}
        <Button
          variant="secondary"
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (!collapsed ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={onToggleCollapse}
          data-testid="prospect-panel-toggle"
        >
          {collapsed ? (
            <PanelRightOpen className="h-4 w-4" />
          ) : (
            <PanelRightClose className="h-4 w-4" />
          )}
        </Button>

        <div className="my-1 h-px w-7 bg-white/10" />

        {/* Prospect Info */}
        <Button
          variant={currentView === "prospect_info" ? "default" : "secondary"}
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (currentView === "prospect_info" ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={() => handleViewClick("prospect_info")}
          data-testid="prospect-panel-icon-prospect"
        >
          <User className="h-4 w-4" />
        </Button>

        {/* Quote Sheet */}
        <Button
          variant={currentView === quoteToolId ? "default" : "secondary"}
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (currentView === quoteToolId ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={() => handleViewClick(quoteToolId)}
          data-testid="prospect-panel-icon-1"
        >
          <List className="h-4 w-4" />
        </Button>

        {/* Calculator */}
        <Button
          variant={currentView === "placeholder_2" ? "default" : "secondary"}
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (currentView === "placeholder_2" ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={() => handleViewClick("placeholder_2")}
          data-testid="prospect-panel-icon-2"
        >
          <Calculator className="h-4 w-4" />
        </Button>

        {/* Calendar */}
        <Button
          variant={currentView === "placeholder_3" ? "default" : "secondary"}
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (currentView === "placeholder_3" ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={() => handleViewClick("placeholder_3")}
          data-testid="prospect-panel-icon-3"
        >
          <CalendarIcon className="h-4 w-4" />
        </Button>

        {/* Notes */}
        <Button
          variant={currentView === "placeholder_4" ? "default" : "secondary"}
          size="icon"
          className={
            "h-9 w-9 rounded-xl ring-1 " +
            (currentView === "placeholder_4" ? SELECTED_ICON_CLASS : IDLE_ICON_CLASS)
          }
          onClick={() => handleViewClick("placeholder_4")}
          data-testid="prospect-panel-icon-4"
        >
          <StickyNote className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default QuickReferenceRail;
